import React, { useEffect, useState } from "react";
import { Truck, Package, IndianRupee, MapPin } from "lucide-react";
import { BsBox } from "react-icons/bs";
import { orderAPI, authAPI } from "../../api";
import "./PickupPage.css";
import AccessModule from "../../components/AccessModule/AccessModule";
import Swal from "sweetalert2";


interface Order {
  _id: string;
  totalAmount: number;
  paymentStatus: string;
  orderId: string;
  customerName: string;
  orderStatus: string;
  products: Product[];
  shippingAddress?: {
    clinicName?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
}

interface Product {
  productId: string;
  seller_id: string;
  productName: string;
  quantity: number;
  price: number;
  image: string;
  sellerName: string;
}

export default function PickupPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const sellerData = JSON.parse(localStorage.getItem("seller") || "{}");
  const sellerId = sellerData?._id;

  const [updating, setUpdating] = useState(false);

  const [accessChecked, setAccessChecked] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const userDetails = JSON.parse(
          localStorage.getItem("seller") || "{}"
        );

        const sellerRes = await authAPI.getSellerStatus(
          userDetails._id
        );

        setVerificationStatus(
          sellerRes.data.verification_status
        );

        setIsBlocked(
          sellerRes.data.isBlock
        );

        setAccessChecked(true);
      } catch (err) {
        console.error(err);
        setAccessChecked(true);
      }
    };

    checkAccess();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);

      const res = await orderAPI.getPickupList({
        currentPage: 1,
        itemsPerPage: 100,
        sellerId: sellerId
      });
      setOrders(res.data.data.item || []);
    } catch (error) {
      console.error("Failed to fetch pickup orders", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessChecked) return;

    if (
      isBlocked ||
      verificationStatus === "P" ||
      verificationStatus === "R"
    ) {
      return;
    }

    fetchOrders();
  }, [accessChecked, isBlocked, verificationStatus]);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "out_for_delivery":
        return "Out for delivery";

      case "pickup":
        return "Pickup";

      default:
        return status;
    }
  };

  const markOutForDelivery = async (
    orderId: string,
    productId: string,
    productName: string
  ) => {
    const result = await Swal.fire({
      title: "Ready for Delivery?",
      html: `
      <div>
        <b>${productName}</b><br/>
        Mark this product as ready for delivery?
      </div>
    `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Ready",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb"
    });

    if (!result.isConfirmed) return;

    try {
      setUpdating(true);

      const res = await orderAPI.outForDelivery({
        orderId,
        productId
      });

      await Swal.fire({
        icon: "success",
        title: "Order Updated",
        text: "The order is now Out for Delivery."
      });

      fetchOrders();

    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Unable to update status."
      });
    } finally {
      setUpdating(false);
    }
  };
  const markDeliveryComplete = async (orderId: string, productId: string, productName: string) => {
    const { value: otp } = await Swal.fire({
      title: 'Enter Delivery OTP',
      html: `
      <div>
        <b>${productName}</b><br/>
        Please enter the OTP provided by the customer to verify delivery.
      </div>
    `,
      input: 'text',
      inputPlaceholder: 'Enter OTP',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      confirmButtonText: 'Verify OTP',
      inputValidator: (value) => {
        if (!value) {
          return 'OTP is required!';
        }
      }
    });

    if (otp) {
      try {
        setUpdating(true);
        const res = await orderAPI.verifyDeliveryOtp({ orderId, productId, otp });

        await Swal.fire({
          icon: 'success',
          title: 'Delivered',
          text: 'Order marked as delivered successfully!'
        });

        fetchOrders();
      } catch (err: any) {
        console.error(err);
        Swal.fire({
          icon: 'error',
          title: 'Verification Failed',
          text: err.response?.data?.message || 'Invalid OTP entered. Please try again.'
        });
      } finally {
        setUpdating(false);
      }
    }
  };

 
  if (
    isBlocked ||
    verificationStatus === "P" ||
    verificationStatus === "R"
  ) {
    return (
      <AccessModule
        isBlock={isBlocked}
        verification_status={verificationStatus}
      />
    );
  }

  return (
    <div className="pickup-page" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
      <div className="pickup-header" style={{ flexShrink: 0, alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Pick Up</h1>
          <p className="page-subtitle">Schedule and manage your order pickups</p>
        </div>
      </div>

      {loading ? (
        <div className="pickup-empty" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Truck size={50} />
          <h3>Loading...</h3>
          <p>Please wait while pickup orders are being loaded.</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="pickup-empty" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Truck size={50} />
          <h3>No Pick Ups Scheduled</h3>
          <p>
            When you have orders ready for pickup, they will appear here for you to
            manage.
          </p>
        </div>
      ) : (
        <div className="pickup-orders-list" style={{ display: "flex", flexDirection: "column", gap: "32px", flex: 1, overflowY: "auto", paddingBottom: "24px" }}>
          {orders.map((order) => {
            const productList = Array.isArray(order.products) ? order.products : (order.products ? [order.products] : []);
            return (
              <div key={order._id} className="order-document" style={{ border: "1px solid #e2e8f0", borderRadius: "18px", padding: "24px", background: "#f8fafc" }}>
                <div className="order-document-header" style={{ marginBottom: "20px", display: "flex", flexDirection: "column", gap: "12px", borderBottom: "1px solid #e2e8f0", paddingBottom: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "24px",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: "#64748b",
                      fontSize: "14px",
                      fontWeight: "500",
                      width: "100%"
                    }}
                  >
                    <div style={{ display: "flex", gap: "24px" }}>
                      <span>
                        Order ID:
                        <span className="order-id-badge">
                          {order.orderId}
                        </span>
                      </span>

                      <span>
                        Customer Name:
                        <span className="customer-name-badge">
                          {order.customerName}
                        </span>
                      </span>
                    </div>
                  </div>
                  {order.shippingAddress && (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "6px", color: "#64748b", fontSize: "13px", marginTop: "4px" }}>
                      <MapPin size={16} color="#10b981" style={{ marginTop: "2px", flexShrink: 0 }} />
                      <div style={{ lineHeight: "1.5" }}>
                        <span style={{ fontWeight: 600, color: "#334155", marginRight: "6px" }}>Delivery Address:</span>
                        {order.shippingAddress.clinicName ? `${order.shippingAddress.clinicName}, ` : ""}
                        {order.shippingAddress.address ? `${order.shippingAddress.address}, ` : ""}
                        {order.shippingAddress.city ? `${order.shippingAddress.city}, ` : ""}
                        {order.shippingAddress.state ? `${order.shippingAddress.state}, ` : ""}
                        {order.shippingAddress.zip ? `${order.shippingAddress.zip}` : ""}
                      </div>
                    </div>
                  )}
                </div>
                <div className="pickup-grid">
                  {productList.map((product: any, index: number) => (
                    <div
                      key={`${order._id}-${index}`}
                      className="pickup-card"
                    >
                      <div className="pickup-card-top">
                        {product.image && typeof product.image === 'string' && product.image.trim() !== "" ? (
                          <img
                            className="pickup-product-image"
                            src={product.image}
                          />
                        ) : (
                          <div
                            className="pickup-product-image"
                            style={{
                              background: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <BsBox size={32} color="#94a3b8" />
                          </div>
                        )}

                        <div className="pickup-product-info">

                          <h3 title={product.productName}>
                            {product.productName && product.productName.length > 18
                              ? `${product.productName.substring(0, 24)}...`
                              : product.productName}
                          </h3>
                          <div><p className="text-sm">Customer Name : {order.customerName}</p></div>
                          <div className="pickup-badges">
                            <span className="badge qty">
                              Quantity {product.quantity}
                            </span>

                            <span className="badge price">
                              ₹{product.price}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="pickup-divider" />

                      <div className="pickup-details">
                        <div>
                          <label>Order ID</label>
                          <span className="order-id">
                            {order.orderId}
                          </span>
                        </div>

                        <div>
                          <label>Order Status</label>
                          <span className={`status-badge ${product.orderStatus}`}>
                            {getStatusLabel(product.orderStatus)}
                          </span>
                        </div>

                        {product.orderStatus === "pickup" ? (
                          <div style={{ marginTop: "12px", width: "100%" }}>
                            <button
                              className="out-delivery-btn"
                              disabled={updating}
                              onClick={(e) => {
                                e.stopPropagation();
                                markOutForDelivery(order._id, product.productId, product.productName);
                              }}
                              style={{ height: "38px", fontSize: "14px", marginTop: "0" }}
                            >
                              {updating ? "Updating..." : "Ready for Delivery"}
                            </button>
                          </div>
                        ) : product.orderStatus === "out_for_delivery" ? (
                          <div style={{ marginTop: "12px", width: "100%" }}>
                            <button
                              className="out-delivery-btn"
                              disabled={updating}
                              onClick={(e) => {
                                e.stopPropagation();
                                markDeliveryComplete(order._id, product.productId, product.productName);
                              }}
                              style={{ height: "38px", fontSize: "14px", marginTop: "0", background: "#10b981" }}
                            >
                              {updating ? "Verifying..." : "Delivery Complete"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}